import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  approveStockRequisitionQuery,
  cancelStockRequisitionQuery,
  createStockRequisitionQuery,
  issueStockRequisitionQuery,
  listStockRequisitionsQuery,
  submitStockRequisitionQuery
} from "../../../Graphql/querries/education/stockRequisitionQuery";
import {
  CreateStockRequisitionInput,
  ListStockRequisitionsResponse,
  StockRequisition,
  StockRequisitionIdInput,
  StockRequisitionMutationResponse
} from "../../../components/Education/StockUsage/types/stockRequisition";
import { GraphQLData, unwrapGraphQLOperation } from "./educationGraphQLUtils";

export const useStockRequisitions = (input?: {
  page?: number;
  size?: number;
}) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["stockRequisitions", input?.page ?? 0],
    queryFn: async (): Promise<StockRequisition[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListStockRequisitionsResponse, "listStockRequisitions">,
        { input: object }
      >(listStockRequisitionsQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listStockRequisitions");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.requisitions ?? [];
    }
  });
};

export const useCreateStockRequisition = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateStockRequisitionInput) => {
      const data = await fetchGraphQL<
        GraphQLData<StockRequisitionMutationResponse, "createStockRequisition">,
        { input: CreateStockRequisitionInput }
      >(createStockRequisitionQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createStockRequisition");
      if (response.code !== API_RESPONSE_CODE.OK || !response.requisition) {
        throw new Error(response.code);
      }
      return response.requisition;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

const useRequisitionAction = (
  query: string,
  operation:
    | "submitStockRequisition"
    | "approveStockRequisition"
    | "issueStockRequisition"
    | "cancelStockRequisition"
) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: StockRequisitionIdInput) => {
      const data = await fetchGraphQL<
        GraphQLData<StockRequisitionMutationResponse, typeof operation>,
        { input: StockRequisitionIdInput }
      >(query, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, operation);
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
      return response.requisition;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useSubmitStockRequisition = () =>
  useRequisitionAction(submitStockRequisitionQuery, "submitStockRequisition");
export const useApproveStockRequisition = () =>
  useRequisitionAction(approveStockRequisitionQuery, "approveStockRequisition");
export const useIssueStockRequisition = () =>
  useRequisitionAction(issueStockRequisitionQuery, "issueStockRequisition");
export const useCancelStockRequisition = () =>
  useRequisitionAction(cancelStockRequisitionQuery, "cancelStockRequisition");
